import React, { Component } from "react";
import {
  VictoryChart,
  VictoryAxis,
  VictoryGroup,
  VictoryBar,
  VictoryTooltip,
  VictoryLegend,
} from "victory";
import CcRealWorldComp from "./cc-real-world-comp";
import "./ChurnCalculator.css";
import SocialShare from "./SocialShare";
import ExplanationContent from "./ExplanationContent";
import CtaEmailForm from "./CtaEmailForm";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});
const decimalFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
});

function _onValueChange(property, value) {
  window.gtag("event", `change-${property}`, { newValue: value });
}

class ChurnCalculator extends Component {
  constructor(props) {
    super(props);

    this.state = {
      numCustomers: 1000,
      numChurned: 50,
      arpa: 30,
      churnImprovement: 10,
      isShowingDetails: false,
    };
  }

  handleInputChange = (value, target) => {
    this.setState({
      [target]: isNaN(value) ? 0 : value,
    });
    _onValueChange(target, value);
  };

  render() {
    const { numCustomers, numChurned, arpa, churnImprovement } = this.state;

    const customersSavedCalc = (
      initial,
      improvement,
      iterations,
      arpaVal,
      currIter = 0,
      sum = 0,
      savedSum = 0,
      grid = []
    ) => {
      if (currIter === iterations) return [sum, savedSum, grid];

      const newlySaved = Math.round((initial - sum) * improvement);
      sum += newlySaved;
      savedSum += sum * arpaVal;
      grid[currIter] = {
        month: currIter,
        newlySaved,
        totalSaved: sum,
        revSavedThisMonth: sum * arpaVal,
        cumulativeRevSaved: savedSum,
      };

      currIter += 1;

      return customersSavedCalc(
        initial,
        improvement,
        iterations,
        arpaVal,
        currIter,
        sum,
        savedSum,
        grid
      );
    };

    const calcedData = customersSavedCalc(
      numChurned,
      churnImprovement / 100,
      12,
      arpa
    );

    return (
      <div>
        <section className="cf pv4-ns ph2 content-section">
          <div className="mw7 center f5-ns f7 ph3">
            <p className="lh-copy">
              Calculating churn rate (aka customer attrition) is straightforward
              but critical for knowing how to improve your bottom line. Enter
              the details for your business on the{" "}
              <span className="user-input-box">gold lines</span> below, using
              your last full month as the reference.
            </p>
            <p className="lh-copy">
              Don't know the exact figures off the top of your head? Don't worry
              about it! Just put in your best guess.
            </p>
          </div>
        </section>

        <section className="cf pv4-ns ph3 ph2-ns content-section">
          <div className="mw8 center">
            <div className="fl w-100 w-50-ns mt4 br-ns bt-0 bb-0 bl-0 b--dashed-ns">
              <div className="pv3-ns pr5-ns hide-child">
                <div className="fl w-100  pr2 pr4-ns">
                  <label className="f3" htmlFor="customersInput">
                    I have{" "}
                    <input
                      id="customersInput"
                      type="number"
                      className="user-input-box ml2"
                      value={numCustomers}
                      onChange={e => {
                        this.handleInputChange(
                          parseInt(e.target.value, 10),
                          "numCustomers"
                        );
                      }}
                    />{" "}
                    total customers.
                  </label>
                </div>
                <p className="fl w-100 f7 i moon-gray child">
                  Enter the total number of customers you had at the{" "}
                  <strong>beginning of last month</strong>, before any ceased
                  being customers.
                </p>
              </div>
              <div className="pv3-ns pr5-ns hide-child">
                <div className="fl w-100  pr2 pr4-ns">
                  <label className="f3" htmlFor="churnedInput">
                    I lost{" "}
                    <input
                      id="churnedInput"
                      type="number"
                      className="user-input-box ml2"
                      value={numChurned}
                      onChange={e => {
                        this.handleInputChange(
                          parseInt(e.target.value, 10),
                          "numChurned"
                        );
                      }}
                    />{" "}
                    customers last month.
                  </label>
                </div>
                <p className="fl w-100 f7 i moon-gray child">
                  Enter the total number of customers that stopped being a
                  customer last month, either through cancellation, expiration
                  of contracts, or payment failures.
                </p>
              </div>
              <div className="pv3-ns pr5-ns hide-child">
                <div className="fl w-100  pr2 pr4-ns">
                  <label className="f3" htmlFor="arpaInput">
                    Each customer account is worth an average of{" "}
                    <span className="nowrap">
                      <span className="pl-gold">$</span>
                      <input
                        id="arpaInput"
                        type="number"
                        className="user-input-box"
                        value={arpa}
                        onChange={e => {
                          this.handleInputChange(
                            parseFloat(e.target.value),
                            "arpa"
                          );
                        }}
                      />
                    </span>{" "}
                    per month.
                  </label>
                </div>
                <p className="fl w-100 f7 i moon-gray child">
                  This is the average value of an account{" "}
                  <strong>per month</strong>. For example, if your service is
                  billed at $300 every quarter, you would enter $100 here.
                </p>
              </div>
            </div>

            <div className="fl w-100 w-50-ns">
              <div className="tc pt5-ns">
                <div className="f1 fw7 light-red">
                  {decimalFormatter.format(numChurned / numCustomers * 100.0)}%
                </div>
                <div className="f4 b">Churn Rate</div>
              </div>

              <div className="tc pv4 pt5-ns pb3-ns">
                <div className="f1 fw7 light-red">
                  {currencyFormatter.format(numChurned * arpa * -1)}
                </div>
                <div className="f4 b">Lost Revenue due to Churn</div>
              </div>
            </div>

            <div className="cf fl w-100 tc i white-60 pt4-ns pb2 f7 tracked">
              All figures above are monthly values
            </div>
          </div>
        </section>

        <section className="cf pv4 ph2 content-section">
          <div className="cf mw8 center">
            <div className="cf fl w-100 tc mb3">
              <p className="f3">
                What if I improved churn by{" "}
                <span className="pl-gold b">{churnImprovement}%</span>{" "}
                month-over-month?
              </p>
              <input
                type="range"
                className="range-input"
                value={churnImprovement}
                max="100"
                min="0"
                step="1"
                onChange={e => {
                  this.handleInputChange(
                    parseInt(e.target.value, 10),
                    "churnImprovement"
                  );
                }}
              />
            </div>

            <div className="fl w-100 w-50-ns tc">
              <div className="f1 fw7 pl-gold">
                {currencyFormatter.format(calcedData[2][0].revSavedThisMonth)}
              </div>
              <div className="f4 b">
                Recovered Revenue<br /> in Month 1
              </div>
            </div>

            <div className="fl w-100 w-50-ns tc mt4 mt0-ns">
              <div className="f1 fw7 pl-gold">
                {currencyFormatter.format(calcedData[1])}
              </div>
              <div className="f4 b">
                Year Cumulative<br /> Recovered Revenue
              </div>
            </div>

            <CcRealWorldComp totalValue={calcedData[1]} />
          </div>

          <div className="cf mw7 center tc mt3" style={{ fontSize: "12px" }}>
            <SocialShare />
          </div>

          <section className="cf ph2">
            <CtaEmailForm
              {...this.state}
              churnRate={decimalFormatter.format(
                numChurned / numCustomers * 100.0
              )}
              month1={calcedData[2][0].revSavedThisMonth}
              yearlyCumulative={calcedData[1]}
              formUrl="https://api.formbucket.com/f/buk_0iMvvCUP8vLCszrDUCh1wZws"
              placement="top"
            />
          </section>
        </section>

        <section className="cf pv4 ph2 content-section">
          <div className="mw8 center">
            <div className="fl w-100 tc">
              <div className="mw6 center">
                <h4 className="f2 lh-title">
                  What!? I can't believe it. Show me the tables and charts!
                </h4>
                <button
                  type="button"
                  className="f4 link hover-gold br1 ba ph3 pv2 mb3 dib yellow b--yellow"
                  style={{ background: "none" }}
                  onClick={() => {
                    this.setState({
                      isShowingDetails: !this.state.isShowingDetails,
                    });
                  }}
                >
                  {this.state.isShowingDetails ? "Hide" : "Show"} Details
                </button>
              </div>
            </div>

            <div
              className={`fl w-100 ${this.state.isShowingDetails ? "" : "dn"}`}
            >
              <table className="f7 w-100 w-80-ns mw8 center" cellSpacing={0}>
                <thead>
                  <tr>
                    <th className="fw6 bb white b--black-20 tl pv1 pr1 bg-dark-gray">
                      &nbsp;
                    </th>
                    <th className="fw6 bb white b--black-20 tl pv1 pr1 bg-dark-gray">
                      New Customers Saved
                    </th>
                    <th className="fw6 bb white b--black-20 tl pv1 pr1 bg-dark-gray">
                      Revenue Saved this Month
                    </th>
                    <th className="fw6 bb white b--black-20 tl pv1 pr1 bg-dark-gray">
                      Cumulative Customers Saved
                    </th>
                    <th className="fw6 bb white b--black-20 tl pv1 pr1 bg-dark-gray">
                      Cumulative Revenue Saved
                    </th>
                  </tr>
                </thead>
                <tbody className="lh-copy">
                  {calcedData[2].map(r => (
                    <tr key={r.month}>
                      <td className="pv1 pr3 bb br b--black-20 bg-pl-dark-blue">
                        Month {r.month + 1}
                      </td>
                      <td className="pv1 pr3 bb b--black-20 bg-pl-dark-blue tc">
                        {r.newlySaved}
                      </td>
                      <td className="pv1 pr3 bb br b--black-20 bg-pl-dark-blue">
                        {currencyFormatter.format(r.revSavedThisMonth)}
                      </td>
                      <td className="pv1 pr3 bb b--black-20 bg-pl-dark-blue tc">
                        {r.totalSaved}
                      </td>
                      <td className="pv1 pr3 bb b--black-20 bg-pl-dark-blue">
                        {currencyFormatter.format(r.cumulativeRevSaved)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              className={`fl w-100 h5 ${this.state.isShowingDetails
                ? ""
                : "dn"}`}
            >
              <VictoryChart domainPadding={{ x: 10 }}>
                <VictoryLegend
                  orientation="horizontal"
                  data={[
                    {
                      name: "Add'l Monthly Revenue",
                      symbol: { fill: "#344e5c" },
                    },
                    { name: "Cumulative Revenue", symbol: { fill: "#4bb19d" } },
                  ]}
                  y={280}
                  x={50}
                />
                <VictoryAxis
                  tickValues={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]}
                  tickFormat={[
                    "M 1",
                    "M 2",
                    "M 3",
                    "M 4",
                    "M 5",
                    "M 6",
                    "M 7",
                    "M 8",
                    "M 9",
                    "M 10",
                    "M 11",
                    "M 12",
                  ]}
                />
                <VictoryAxis dependentAxis tickFormat={x => `$${x / 1000}k`} />
                <VictoryGroup
                  colorScale={"qualitative"}
                  offset={10}
                  labels={d =>
                    `Saved this month: $${d.revSavedThisMonth}\n Cumulative Saved: $${d.cumulativeRevSaved}`}
                  labelComponent={<VictoryTooltip />}
                >
                  <VictoryBar
                    data={calcedData[2]}
                    x="month"
                    y="revSavedThisMonth"
                  />
                  <VictoryBar
                    data={calcedData[2]}
                    x="month"
                    y="cumulativeRevSaved"
                  />
                </VictoryGroup>
              </VictoryChart>
            </div>
          </div>
        </section>

        <ExplanationContent
          totalCustomers={this.state.numCustomers}
          totalChurn={this.state.numChurned}
          churnRate={this.state.numChurned / this.state.numCustomers * 100.0}
          improvement={this.state.churnImprovement}
          yearlyCumulative={currencyFormatter.format(calcedData[1])}
        />

        <section className="cf pv4 ph2 content-section">
          <CtaEmailForm
            {...this.state}
            churnRate={decimalFormatter.format(
              numChurned / numCustomers * 100.0
            )}
            month1={calcedData[2][0].revSavedThisMonth}
            yearlyCumulative={calcedData[1]}
            formUrl="https://api.formbucket.com/f/buk_0iMvvCUP8vLCszrDUCh1wZws"
            placement="bottom"
          />
        </section>
      </div>
    );
  }
}

export default ChurnCalculator;



// WEBPACK FOOTER //
// ./src/ChurnCalculator.jsx